import {post} from './Request';
import {CONFIG} from '../../globals/config';
import {setGlobalImmer} from 'use-immer-reactn';
import sum from 'lodash/sum';
import chunk from 'lodash/chunk';
import filter from 'lodash/filter';
import {getState} from '../../globals/state';

export type TableCounts = {
	[ table: string ]: number;
}

export async function resetCounts(): Promise<void> {
	await setGlobalImmer( draft => {
		Object.keys( draft.tables ).forEach( table => {
			draft.tables[ table ].count = 0;
		} );
		Object.keys( draft.sections ).forEach( section => {
			draft.sections[ section ].count = 0;
		} );
	} );
}

/**
 * Break tables/sections into chunks and submit the
 * update or count request on each.
 *
 * If we are using sections we send all a section's tables
 * at once, if not use break tables into 5 at a time.
 *
 * @param  countOnly
 */
export async function submit( countOnly: boolean = true ): Promise<number> {
	let totalCount = 0;
	await resetCounts();
	if ( getState( 'isSections' ) ) {
		const sections = getState( 'sections' );
		for ( const section of Object.keys( sections ) ) {
			if ( sections[ section ].checked ) {
				totalCount += await updateTables( CONFIG.sections[ section ].tables, countOnly, section );
			}
		}
	} else {
		const checkedTables = filter( getState( 'tables' ), o => o.checked );
		// Do 5 tables at a time.
		for ( const tables of chunk( checkedTables.map( o => o.label ), 5 ) ) {
			totalCount += await updateTables( tables, countOnly );
		}
	}

	return totalCount;
}

/**
 * Sends an AJAX request to update a list of tables and
 * updates the state of each one upon completion or failure.
 *
 * All changes to status and count states are done within
 * this process.
 *
 * @param  tables
 * @param  countOnly
 * @param  section
 */
async function updateTables( tables: string[], countOnly: boolean = true, section?: string ): Promise<number> {
	const start = countOnly ? 'counting' : 'updating';
	const finish = countOnly ? 'counted' : 'updated';
	const newUrl = getState( 'newUrl' );
	const oldUrl = getState( 'oldUrl' );

	// Stop any counting requests if an update is occurring.
	if ( ( countOnly && getState( 'updating' ) ) || '' === newUrl || '' === oldUrl ) {
		return 0;
	}

	try {
		setGlobalImmer( draft => {
			tables.forEach( table => {
				draft.tables[ table ].status = start;
			} );
			if ( section ) {
				draft.sections[ section ].status = start;
			}
		} );

		const counts = await post<TableCounts>( CONFIG.endpoint.actions.updateUrls, {
			[ CONFIG.fields.newUrl ]: newUrl,
			[ CONFIG.fields.oldUrl ]: oldUrl,
			[ CONFIG.fields.tables ]: tables,
			[ CONFIG.fields.countOnly ]: countOnly,
		} );

		const totalCount = sum( Object.values( counts ) );
		setGlobalImmer( draft => {
			tables.forEach( table => {
				if ( counts.hasOwnProperty( table ) ) {
					draft.tables[ table ].status = finish;
					draft.tables[ table ].count = counts[ table ];
				} else {
					draft.tables[ table ].status = 'failed';
				}
			} );
			if ( section ) {
				draft.sections[ section ].status = finish;
				draft.sections[ section ].count = totalCount;
			}
		} );
		return totalCount;
	} catch ( e ) {
		console.error( e );
		await fail( tables, section );
		return 0;
	}
}


async function fail( tables: string[], section?: string ) {
	await setGlobalImmer( draft => {
		tables.forEach( table => {
			draft.tables[ table ].status = 'failed';
		} );
		if ( section ) {
			draft.sections[ section ].status = 'failed';
		}
	} );
}
